// @flow

/**
 * HUD voucher adjustment shapes — aligned with fortress-backend
 * `src/domain/types/hudVoucherAdjustment.js` (API / Sequelize row).
 */

export type HudVoucherAdjustmentHouseholdSnapshot = {
  householdId: string,
  headOfHouseholdFirstName: string,
  headOfHouseholdLastName: string,
  headOfHouseholdInitial: string | null,
  unitNumber: string,
  unitId: string | null,
};

export type HudVoucherAdjustmentBillingType = 'prior_billing' | 'new_billing';

/** UT-O (out of the old unit) / UT-I (into the new unit) — Part 3 splits a transfer in two. */
export type HudVoucherAdjustmentTransferDirection = 'OUT' | 'IN';

/** One row from GET .../hud-vouchers/:voucherId/adjustments */
export type HudVoucherAdjustmentApi = {
  id: string,
  voucherId: string,
  householdId: string,
  triggeringHudTenantEventId: string | null,
  sortOrder: number,
  affordableQualificationId: string | null,
  adjustmentBlockId: string | null,
  certificationType: string | null,
  householdSnapshot: HudVoucherAdjustmentHouseholdSnapshot,
  unitId: string | null,
  unitNumber: string | null,
  transferDirection: HudVoucherAdjustmentTransferDirection | null,
  /** `MasterMoveOutCode.code` ('4' = Death of sole family member); absent until the backend column ships. */
  moveOutCode?: string | null,
  billingType: HudVoucherAdjustmentBillingType,
  isNewCertification: boolean,
  effectiveDate: string | null,
  beginningDate: string | null,
  endingDate: string | null,
  // DECIMAL / numeric columns may arrive as strings from pg via the API
  assistancePayment: string | number | null,
  beginningPartialMonthNumberOfDays: string | number | null,
  beginningPartialMonthDailyRate: string | number | null,
  fullMonthNumberOfMonths: string | number | null,
  fullMonthMonthlyRate: string | number | null,
  endingPartialMonthNumberOfDays: string | number | null,
  endingPartialMonthDailyRate: string | number | null,
  amount: string | number | null,
  requestedAmount: string | number | null,
  approvedAmount: string | number | null,
  caHudApprovedAmount: string | number | null,
  createdAutomatically: boolean,
  createdAt?: string,
  updatedAt?: string,
};

/** API fields renamed for table columns (spread into the full row). */
export type HudVoucherAdjustmentTableMappedScalars = {|
  effectiveDate: string | null,
  assistancePayment: number | null,
  requested: number | null,
  approvedAmount: number | null,
  adjustmentBeginDate: string | null,
  adjustmentEndDate: string | null,
  daysBeginningPartialMonth: number | null,
  dailyRateBeginningPartialMonth: number | null,
  fullMonths: number | null,
  fullMonthlyRate: number | null,
  daysEndingPartialMonth: number | null,
  dailyRateEndingPartialMonth: number | null,
  calculationDetailsAmount: number | null,
|};

/** All mapped scalar fields null (e.g. spacer rows). */
export const HUD_VOUCHER_ADJUSTMENT_MAPPED_SCALARS_ALL_NULL: HudVoucherAdjustmentTableMappedScalars =
  {
    effectiveDate: null,
    assistancePayment: null,
    requested: null,
    approvedAmount: null,
    adjustmentBeginDate: null,
    adjustmentEndDate: null,
    daysBeginningPartialMonth: null,
    dailyRateBeginningPartialMonth: null,
    fullMonths: null,
    fullMonthlyRate: null,
    daysEndingPartialMonth: null,
    dailyRateEndingPartialMonth: null,
    calculationDetailsAmount: null,
  };

/**
 * Row shape consumed by Adjustments table columns / `useRows`.
 * Single exact object (not `&` intersection) so Flow accepts `{ ...mapped, id, ... }`.
 */
export type HudVoucherAdjustmentTableRow = {|
  ...HudVoucherAdjustmentTableMappedScalars,
  id: string,
  adjustmentBlockId: string | null,
  status: string,
  headOfHousehold: string,
  unit: string,
  hudUnitNumber: string,
  priorOrNew: string,
  newCert: string,
  certType: string,
  isBlockSpacer?: boolean,
  isUnitDivider?: boolean,
|};
